import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi } from './api';
import type { MessageAffaire } from './api';

/**
 * LE FIL D'INSTRUCTION — les messages d'une affaire.
 *
 * ⚠️⚠️ UN MESSAGE COUVERT N'EST PAS UN MESSAGE VIDE, ET L'ÉCRAN DOIT LE DIRE.
 * La portée restreint la LECTURE, jamais la TRACE : un message qu'on n'a pas le
 * droit de lire apparaît au fil avec son auteur, sa date et sa portée — seul son
 * texte manque. Afficher une ligne blanche à la place se lirait « rien à voir »,
 * et un registre où l'existence d'un échange serait invisible ne serait pas
 * auditable.
 *
 * ⚠️ LA PORTÉE SE CHOISIT AVANT D'ÉCRIRE, PAS APRÈS. Le fil est en ajout seul
 * (migration 245) : rien ne se corrige, rien ne s'efface. Un champ de cible qui
 * n'apparaîtrait qu'après la saisie ferait écrire d'abord et réfléchir ensuite.
 */

const LIBELLE_PORTEE: Record<MessageAffaire['portee'], string> = {
  tous: 'tous les intervenants',
  metier: 'un métier',
  agent: 'un agent nommé',
  service: 'un service',
};

const AIDE_CIBLE: Record<Exclude<MessageAffaire['portee'], 'tous'>, string> = {
  metier: 'Le métier qui pourra lire — par exemple geometre, notaire, agent_fiscal.',
  agent: "L'identifiant du compte qui pourra lire.",
  service: "L'identifiant du service qui pourra lire.",
};

function quand(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR');
}

export function FilAffaire({ reference }: { reference: string }) {
  const [fil, setFil] = useState<MessageAffaire[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [portee, setPortee] = useState<MessageAffaire['portee']>('tous');
  const [cible, setCible] = useState('');
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await api.affaires.messages(reference);
      setFil(r.messages);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Le fil n’a pas pu être lu.');
      setFil([]);
    }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  const deposer = async () => {
    if (texte.trim().length < 2) { setErreur('Un message tient en deux caractères au moins.'); return; }
    if (portee !== 'tous' && cible.trim() === '') {
      setErreur('Une portée restreinte doit désigner qui peut lire.');
      return;
    }
    setEnvoi(true);
    try {
      await api.affaires.deposerMessage(reference, {
        portee, porteeCible: portee === 'tous' ? null : cible.trim(), contenu: texte.trim(),
      });
      setTexte(''); setCible(''); setPortee('tous'); setErreur(null);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Le message n’a pas pu être déposé.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <section className="fil-affaire">
      <h4>Messages de l’affaire</h4>
      <p className="aide">
        Un message se dépose sur l’affaire, jamais à côté. Sa portée décide qui peut le
        lire. Rien ne s’efface : le fil est en ajout seul, comme le journal.
      </p>

      <div className="fil-depot">
        <label className="fil-champ">
          <span>Portée du message</span>
          <select value={portee} onChange={(e) => setPortee(e.target.value as MessageAffaire['portee'])}>
            {(Object.keys(LIBELLE_PORTEE) as MessageAffaire['portee'][]).map((p) => (
              <option key={p} value={p}>{LIBELLE_PORTEE[p]}</option>
            ))}
          </select>
        </label>

        {portee !== 'tous' && (
          <label className="fil-champ">
            <span>Qui exactement</span>
            <input value={cible} onChange={(e) => setCible(e.target.value)}
                   placeholder={AIDE_CIBLE[portee]} aria-describedby="fil-aide-cible" />
            <small id="fil-aide-cible" className="aide">{AIDE_CIBLE[portee]}</small>
          </label>
        )}

        <label className="fil-champ">
          <span>Message</span>
          <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={3}
                    placeholder="Ce que l’instruction doit retenir." />
        </label>

        <button className="bouton" type="button" onClick={() => void deposer()} disabled={envoi}>
          {envoi ? 'Dépôt…' : 'Déposer le message'}
        </button>

        <p className="fil-affaire-note">
          <strong>Ce que la portée ne fait pas :</strong> elle restreint la lecture, pas la
          trace. Un message couvert existe au journal pour tout le monde — on voit qu’il a
          été déposé, par qui et quand ; seul son contenu suit la portée.
        </p>
      </div>

      {erreur && <p className="parcours-empechement">{erreur}</p>}

      {fil === null && <p className="aide">Chargement du fil…</p>}
      {fil !== null && fil.length === 0 && !erreur && (
        <p className="aide">Aucun message sur cette affaire.</p>
      )}

      <ol className="fil-liste">
        {(fil ?? []).map((m) => (
          <li key={m.id} className={m.couvert ? 'fil-message couvert' : 'fil-message'}>
            <div className="parcours-ligne">
              <strong>{m.auteur}</strong>
              <span className="aide">{quand(m.deposeLe)}</span>
              <span className="jeton">
                {LIBELLE_PORTEE[m.portee]}{m.porteeCible ? ` · ${m.porteeCible}` : ''}
              </span>
            </div>
            {m.couvert
              ? <p className="fil-couvert">
                  Message couvert par sa portée — son dépôt est tracé, son contenu ne vous
                  est pas ouvert.
                </p>
              : <p className="fil-contenu">{m.contenu}</p>}
          </li>
        ))}
      </ol>
    </section>
  );
}
